import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { authService } from '../services/api'
import { useTheme } from '../context/ThemeContext'
import ThemeModeSlider from '../components/ThemeModeSlider'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()
  const { isDark, setTheme, themeOptions } = useTheme()

  // Check for logout reason on component mount
  useEffect(() => {
    const logoutReason = sessionStorage.getItem('logoutReason')
    const logoutCode = sessionStorage.getItem('logoutCode')

    if (logoutReason) {
      if (logoutCode === 'SESSION_INVALID') {
        setError('Your account was logged in from another device. You have been logged out here.')
      } else if (logoutCode === 'TOKEN_EXPIRED') {
        setError('Your session expired. Please login again.')
      } else {
        setError(logoutReason)
      }

      // Clear the stored reason
      sessionStorage.removeItem('logoutReason')
      sessionStorage.removeItem('logoutCode')
    }
  }, [])

  const pageClass = isDark
    ? 'min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.22),_transparent_32%),linear-gradient(135deg,_#081120_0%,_#0f172a_42%,_#172554_100%)] text-white'
    : 'min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(96,165,250,0.16),_transparent_32%),linear-gradient(135deg,_#f8fafc_0%,_#e2e8f0_52%,_#dbeafe_100%)] text-slate-900'

  const glassClass = isDark
    ? 'border-white/15 bg-white/10 shadow-[0_28px_80px_rgba(15,23,42,0.45)] backdrop-blur-xl'
    : 'border-slate-200 bg-white/75 shadow-[0_28px_80px_rgba(15,23,42,0.12)] backdrop-blur-xl'

  const panelClass = isDark
    ? 'bg-slate-950/80 ring-1 ring-white/10'
    : 'bg-white/90 ring-1 ring-slate-200/80'

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const response = await authService.login(email, password)

      const { user, token } = response.data
      if (!user || !token) {
        throw new Error('Missing user or token in response')
      }
      
      login(user, token)
      navigate('/dashboard')
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Login failed'
      console.error('❌ Login error:', err)
      setError(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={pageClass}>
      <div className={`absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:48px_48px] ${isDark ? 'opacity-30' : 'opacity-20'}`} />
      <div className="absolute right-6 top-6 z-20">
        <ThemeModeSlider
          isDark={isDark}
          onToggle={() => setTheme(isDark ? themeOptions.LIGHT : themeOptions.DARK)}
        />
      </div>
      <div className="relative mx-auto flex min-h-screen max-w-7xl items-center px-6 py-16 lg:px-12">
        <div className="grid w-full gap-12 lg:grid-cols-[1fr_0.95fr] lg:items-center">
          <div className="max-w-2xl">
            <div className={`inline-flex items-center rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] shadow-lg backdrop-blur ${isDark ? 'border-white/15 bg-white/10 text-sky-100' : 'border-slate-300 bg-white text-sky-700'}`}>
              PROBARA GIGIH SDN BHD
            </div>
            <h1 className={`mt-6 text-5xl font-black leading-tight sm:text-6xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Welcome back to OMS
            </h1>
            <p className={`mt-6 max-w-xl text-lg leading-8 ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
              Continue managing projects, invoices, timesheets, payroll, quotations, and expenses in one centralized workspace.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/"
                className={`inline-flex items-center justify-center rounded-full border px-6 py-3 text-sm font-semibold transition-colors duration-200 ${
                  isDark
                    ? 'border-white/20 text-white/90 hover:bg-white/10'
                    : 'border-slate-300 text-slate-700 hover:bg-slate-100'
                }`}
              >
                Back to welcome
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className={`absolute -inset-6 rounded-[2rem] blur-3xl ${isDark ? 'bg-sky-500/20' : 'bg-sky-400/15'}`} />
            <div className={`relative rounded-[2rem] border p-6 ${glassClass}`}>
              <div className={`rounded-3xl p-6 sm:p-8 ${panelClass}`}>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Login</h2>
                <p className={`mt-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Sign in to continue to your dashboard.</p>

                {error && (
                  <div className={`mt-5 rounded-xl border p-4 text-sm ${isDark ? 'border-red-400/30 bg-red-500/15 text-red-200' : 'border-red-200 bg-red-50 text-red-700'}`}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  <div>
                    <label className={`mb-1 block text-sm font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`w-full rounded-xl border px-4 py-2.5 outline-none transition placeholder-slate-400 focus:ring-2 ${isDark ? 'border-white/20 bg-slate-900/70 text-white focus:border-sky-400 focus:ring-sky-500/35' : 'border-slate-300 bg-white text-slate-900 focus:border-sky-500 focus:ring-sky-200'}`}
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                  <div>
                    <label className={`mb-1 block text-sm font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`w-full rounded-xl border px-4 py-2.5 pr-12 outline-none transition placeholder-slate-400 focus:ring-2 ${isDark ? 'border-white/20 bg-slate-900/70 text-white focus:border-sky-400 focus:ring-sky-500/35' : 'border-slate-300 bg-white text-slate-900 focus:border-sky-500 focus:ring-sky-200'}`}
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-300 hover:text-sky-300' : 'text-slate-500 hover:text-slate-800'}`}
                      >
                        {showPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                            <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l2.1 2.1A12.08 12.08 0 0 0 .25 12a12.35 12.35 0 0 0 4.7 4.9A11.96 11.96 0 0 0 12 19.25a12 12 0 0 0 5.14-1.14l3.33 3.33a.75.75 0 1 0 1.06-1.06L3.53 2.47ZM12 15.75A3.75 3.75 0 0 1 8.25 12c0-.58.13-1.14.37-1.63l5.01 5.01c-.49.24-1.05.37-1.63.37Zm3.38-1.06-5.07-5.07A3.75 3.75 0 0 1 15.75 12c0 .97-.37 1.85-.97 2.5ZM12 4.75c4.02 0 7.54 2.2 9.26 5.47a.75.75 0 0 1 0 .56c-.54 1.03-1.3 1.93-2.21 2.65l-1.09-1.09A5.25 5.25 0 0 0 11.66 6.04l-1.17-1.17A12.7 12.7 0 0 1 12 4.75Z" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                            <path d="M12 4.75c-5.02 0-9.29 3.06-11.1 7.25a.75.75 0 0 0 0 .6C2.7 16.79 6.98 19.25 12 19.25s9.29-2.46 11.1-6.65a.75.75 0 0 0 0-.6C21.29 7.81 17.02 4.75 12 4.75Zm0 11.5a4.75 4.75 0 1 1 0-9.5 4.75 4.75 0 0 1 0 9.5Zm0-2a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5Z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`mt-2 w-full rounded-xl px-4 py-2.5 font-semibold transition disabled:cursor-not-allowed disabled:opacity-70 ${isDark ? 'bg-sky-500 text-slate-950 hover:bg-sky-400' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
                  >
                    {isLoading ? 'Logging in...' : 'Login'}
                  </button>
                </form>

                <p className={`mt-5 text-center text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  Demo credentials: admin@example.com / password
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
